import React, { useEffect, useState } from 'react';
import { databaseService } from '../../services/database.service';
import { storageService } from '../../services/storage.service';
import { i18nService } from '../../services/i18n.service';
import AdminLayout from '../../components/layout/AdminLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Textarea from '../../components/common/Textarea';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import toast from 'react-hot-toast';
import { FaPlus, FaEdit, FaTrash, FaImage } from 'react-icons/fa';

const GalleryManager = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    displayOrder: 1,
    published: true,
    mediaType: 'image', // 'image' or 'video'
    videoSource: 'url', // 'upload' or 'url'
    imageFile: null,
    videoFile: null,
    videoUrl: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [galleryItems, galleryCategories] = await Promise.all([
        databaseService.getAllGalleryItems(),
        databaseService.getGalleryCategories()
      ]);
      setItems(galleryItems);
      setCategories(galleryCategories);
    } catch (error) {
      toast.error('Failed to load gallery data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e, type = 'image') => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = type === 'video' ? 50 * 1024 * 1024 : 5 * 1024 * 1024; // 50MB for video, 5MB for image
      if (file.size > maxSize) {
        toast.error(`File size must be less than ${type === 'video' ? '50MB' : '5MB'}`);
        return;
      }
      if (type === 'video') {
        setFormData({ ...formData, videoFile: file });
      } else {
        setFormData({ ...formData, imageFile: file });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation based on media type
    if (formData.mediaType === 'image' && !formData.imageFile && !selectedItem) {
      toast.error('Please select an image');
      return;
    }

    if (formData.mediaType === 'video') {
      if (formData.videoSource === 'upload' && !formData.videoFile && !selectedItem) {
        toast.error('Please select a video file');
        return;
      }
      if (formData.videoSource === 'url' && !formData.videoUrl && !selectedItem) {
        toast.error('Please enter a video URL');
        return;
      }
    }

    setIsUploading(true);
    try {
      let imageUrl = selectedItem?.imageUrl || '';
      let imagePath = selectedItem?.imagePath || '';
      let videoUrl = selectedItem?.videoUrl || '';

      // Handle image upload
      if (formData.mediaType === 'image' && formData.imageFile) {
        const result = await storageService.uploadImage(
          formData.imageFile,
          'gallery',
          (progress) => setUploadProgress(progress)
        );
        imageUrl = result.url;
        imagePath = result.path;

        // Delete old media if updating
        if (selectedItem?.imagePath) {
          await storageService.deleteImage(selectedItem.imagePath);
        }
      }

      // Handle video upload
      if (formData.mediaType === 'video' && formData.videoSource === 'upload' && formData.videoFile) {
        const result = await storageService.uploadFile(
          formData.videoFile,
          'gallery/videos',
          (progress) => setUploadProgress(progress)
        );
        videoUrl = result.url;
        imagePath = result.path;

        // Delete old media if updating
        if (selectedItem?.imagePath) {
          await storageService.deleteImage(selectedItem.imagePath);
        }
      }

      // Handle video URL
      if (formData.mediaType === 'video' && formData.videoSource === 'url') {
        videoUrl = formData.videoUrl;
        imageUrl = '';
        imagePath = '';
      }

      const itemData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        displayOrder: Number(formData.displayOrder),
        published: formData.published,
        mediaType: formData.mediaType,
        videoSource: formData.mediaType === 'video' ? formData.videoSource : '',
        imageUrl: imageUrl || '',
        imagePath: imagePath || '',
        videoUrl: videoUrl || ''
      };

      if (selectedItem) {
        await databaseService.update(`website/gallery/items/${selectedItem.id}`, itemData);
        toast.success('Item updated successfully');
      } else {
        await databaseService.create('website/gallery/items', itemData);
        toast.success('Item added successfully');
      }

      setShowAddModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save item');
      console.error(error);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setFormData({
      title: item.title,
      description: item.description || '',
      category: item.category,
      displayOrder: item.displayOrder,
      published: item.published,
      mediaType: item.mediaType || 'image',
      videoSource: item.videoSource || 'url',
      imageFile: null,
      videoFile: null,
      videoUrl: item.videoUrl || ''
    });
    setShowAddModal(true);
  };

  const handleDelete = async () => {
    try {
      if (selectedItem.imagePath) {
        await storageService.deleteImage(selectedItem.imagePath);
      }
      await databaseService.delete(`website/gallery/items/${selectedItem.id}`);
      toast.success('Item deleted successfully');
      setShowDeleteDialog(false);
      setSelectedItem(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: '',
      displayOrder: 1,
      published: true,
      mediaType: 'image',
      videoSource: 'url',
      imageFile: null,
      videoFile: null,
      videoUrl: ''
    });
    setSelectedItem(null);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gallery Manager</h1>
            <p className="text-gray-600">Manage your gallery items</p>
          </div>
          <Button
            variant="primary"
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
          >
            <FaPlus className="mr-2" /> Add New Item
          </Button>
        </div>

        {/* Gallery Items Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item) => (
            <Card key={item.id} padding="none">
              {item.mediaType === 'video' ? (
                <div className="relative w-full h-48 bg-gray-900 rounded-t-lg">
                  <iframe
                    src={item.videoUrl}
                    title={item.title}
                    className="w-full h-full rounded-t-lg"
                    allowFullScreen
                  />
                </div>
              ) : (
                <img
                  src={item.imageUrl}
                  alt={item.title}
                  className="w-full h-48 object-cover rounded-t-lg"
                />
              )}
              <div className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-lg">{item.title}</h3>
                  <span className="text-xs px-2 py-0.5 rounded bg-gray-200 text-gray-600">
                    {item.mediaType === 'video' ? '🎥 Video' : '🖼️ Image'}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-2">{item.category}</p>
                <div className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-1 rounded ${item.published ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`}>
                    {item.published ? 'Published' : 'Draft'}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(item)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedItem(item);
                        setShowDeleteDialog(true);
                      }}
                      className="text-red-600 hover:text-red-800"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {items.length === 0 && (
          <Card className="text-center py-12">
            <FaImage className="text-gray-400 text-6xl mx-auto mb-4" />
            <p className="text-gray-600 mb-4">No gallery items yet</p>
            <Button variant="primary" onClick={() => setShowAddModal(true)}>
              Add Your First Item
            </Button>
          </Card>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          resetForm();
        }}
        title={selectedItem ? 'Edit Gallery Item' : 'Add Gallery Item'}
        size="lg"
      >
        <form onSubmit={handleSubmit}>
          <Input
            label="Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
            placeholder="Event title"
          />

          <Textarea
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Brief description (optional)"
          />

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              required
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-gray-900 bg-white"
            >
              <option value="" className="text-gray-500">Select category</option>
              {categories.map((cat) => {
                const categoryName = i18nService.getFirstAvailable(i18nService.extractMultiLangField(cat, 'name'), 'nl') || cat.name || cat.id;
                return (
                  <option key={cat.id} value={categoryName} className="text-gray-900">
                    {categoryName}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Media Type <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="image"
                  checked={formData.mediaType === 'image'}
                  onChange={(e) => setFormData({ ...formData, mediaType: e.target.value })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Image</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="video"
                  checked={formData.mediaType === 'video'}
                  onChange={(e) => setFormData({ ...formData, mediaType: e.target.value })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Video</span>
              </label>
            </div>
          </div>

          {formData.mediaType === 'image' ? (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Image {!selectedItem && <span className="text-red-500">*</span>}
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, 'image')}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-600 hover:file:bg-primary-100"
              />
              {formData.imageFile && (
                <p className="text-sm text-gray-600 mt-1">Selected: {formData.imageFile.name}</p>
              )}
            </div>
          ) : (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Video Source <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="upload"
                      checked={formData.videoSource === 'upload'}
                      onChange={(e) => setFormData({ ...formData, videoSource: e.target.value })}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Upload Video File</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="url"
                      checked={formData.videoSource === 'url'}
                      onChange={(e) => setFormData({ ...formData, videoSource: e.target.value })}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Video URL (YouTube/Vimeo)</span>
                  </label>
                </div>
              </div>

              {formData.videoSource === 'upload' ? (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Video File {!selectedItem && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="file"
                    accept="video/*"
                    onChange={(e) => handleFileChange(e, 'video')}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-600 hover:file:bg-primary-100"
                  />
                  {formData.videoFile && (
                    <p className="text-sm text-gray-600 mt-1">Selected: {formData.videoFile.name}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Max size: 50MB. Supported formats: MP4, WebM, MOV
                  </p>
                </div>
              ) : (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Video URL {!selectedItem && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="url"
                    value={formData.videoUrl}
                    onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
                    placeholder="https://www.youtube.com/embed/VIDEO_ID"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    required={formData.mediaType === 'video' && formData.videoSource === 'url' && !selectedItem}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Supported: YouTube embed URLs, Vimeo embed URLs
                  </p>
                </div>
              )}
            </>
          )}

          <Input
            label="Display Order"
            type="number"
            value={formData.displayOrder}
            onChange={(e) => setFormData({ ...formData, displayOrder: e.target.value })}
            required
          />

          <div className="mb-4 flex items-center">
            <input
              type="checkbox"
              id="published"
              checked={formData.published}
              onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="published" className="ml-2 text-sm text-gray-700">
              Publish immediately
            </label>
          </div>

          {uploadProgress > 0 && uploadProgress < 100 && (
            <div className="mb-4">
              <div className="bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600 mt-1">{Math.round(uploadProgress)}% uploaded</p>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowAddModal(false);
                resetForm();
              }}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isUploading} disabled={isUploading}>
              {isUploading ? 'Saving...' : selectedItem ? 'Update Item' : 'Add Item'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title="Delete Gallery Item"
        message="Are you sure you want to delete this item? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
      />
    </AdminLayout>
  );
};

export default GalleryManager;
